import type {
  BlockAttribute,
  BlockConfig,
  GeometryDataTyped,
  Point3,
} from "@/types/blocks.types";
import { DR_BLOCKS, BLOCK_PREFIX } from "./block-list";
import { cloneDeep } from "lodash";

export { BLOCK_PREFIX };

export type { BlockConfig };

export default class BlockLibrary {
  // Returns a clone of the hatch pattern
  static getByName(name: string): BlockConfig | undefined {
    for (const block of DR_BLOCKS) {
      if (block.name === name) {
        const clone = cloneDeep(block);
        return clone;
      }
    }
  }

  static getAllNames(): string[] {
    return DR_BLOCKS.map((block) => block.name);
  }

  static isDRBlock(name: string): boolean {
    return name.startsWith(BLOCK_PREFIX);
  }

  static getBlockConfigName(fullName: string): string {
    if (fullName.startsWith(BLOCK_PREFIX)) {
      return fullName.substring(BLOCK_PREFIX.length);
    }
    return fullName;
  }

  private static scalePoint3(p: Point3, f: number): Point3 {
    return [p[0] * f, p[1] * f, p[2] * f];
  }

  static scaleAttribute(
    attribute: BlockAttribute,
    factor: number
  ): BlockAttribute {
    const s = (p: Point3) => BlockLibrary.scalePoint3(p, factor);
    return {
      ...attribute,
      position: s(attribute.position),
      height:
        attribute.height !== undefined ? attribute.height * factor : undefined,
    };
  }

  static scaleGeometry(
    geometry: GeometryDataTyped,
    factor: number
  ): GeometryDataTyped {
    const s = (p: Point3) => BlockLibrary.scalePoint3(p, factor);
    switch (geometry.type) {
      case "polyline":
        return { ...geometry, points: geometry.points.map(s) };
      case "circle":
        return {
          ...geometry,
          center: s(geometry.center),
          radius: geometry.radius * factor,
        };
      case "ellipse":
        return {
          ...geometry,
          center: s(geometry.center),
          minorPoint: s(geometry.minorPoint),
          majorPoint: s(geometry.majorPoint),
        };
      case "polygon":
        return { ...geometry, corners: geometry.corners.map(s) };
      case "circle-arc":
        return {
          ...geometry,
          start: s(geometry.start),
          middle: s(geometry.middle),
          end: s(geometry.end),
        };
      case "text":
        return {
          ...geometry,
          refpoint: s(geometry.refpoint),
          height:
            geometry.height !== undefined
              ? geometry.height * factor
              : undefined,
        };
    }
  }

  static scaleBlockConfig(block: BlockConfig, factor: number): BlockConfig {
    return {
      ...block,
      geometries: block.geometries.map((g) =>
        BlockLibrary.scaleGeometry(g, factor)
      ),
      attributes: block.attributes?.map((a) =>
        BlockLibrary.scaleAttribute(a, factor)
      ),
    };
  }

  // angle in radian
  static rotateVector(
    v: [x: number, y: number],
    angle: number
  ): [x: number, y: number] {
    return [
      v[0] * Math.cos(angle) - v[1] * Math.sin(angle),
      v[0] * Math.sin(angle) + v[1] * Math.cos(angle),
    ];
  }
}
